import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, cubicBezier } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { useRegion } from '../../src/contexts/RegionContext';

interface PageHeaderProps {
    title: string;
    showBack?: boolean;
    rightAction?: React.ReactNode;
    className?: string;
    isSticky?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({
    title,
    showBack = false,
    rightAction,
    className = "",
    isSticky = false
}) => {
    const navigate = useNavigate();
    const { scrollY } = useScroll();

    // Apple-style scroll physics (Weighted Easing)
    const appleEase = cubicBezier(0.32, 0.72, 0, 1);
    const { theme } = useTheme();
    const { config } = useRegion();
    const titleScale = useTransform(scrollY, [0, 100], [1, 0.96], { ease: appleEase });
    const titleY = useTransform(scrollY, [0, 100], [0, 4], { ease: appleEase });
    const headerBgOpacity = useTransform(scrollY, [0, 60], [0, 0.95], { ease: appleEase });
    const borderOpacity = useTransform(scrollY, [0, 60], [0, 0.05], { ease: appleEase });
    const headerBg = useTransform(
        headerBgOpacity,
        o => theme === 'dark'
            ? `rgba(18, 18, 18, ${o})`
            : `rgba(255, 255, 255, ${o})`
    );

    const borderColor = useTransform(
        borderOpacity,
        o => theme === 'dark'
            ? `rgba(255, 255, 255, ${o})`
            : `rgba(0, 0, 0, ${o})`
    );

    return (
        <motion.div
            style={isSticky ? {
                backgroundColor: headerBg,
                borderBottom: `1px solid ${borderColor}`,
                backdropFilter: 'blur(10px)'
            } : {}}
            className={`flex items-end justify-between px-4 pb-4 pt-8 md:pt-12 transition-colors ${isSticky ? 'sticky top-14 md:top-16 z-40' : ''} ${className}`}
        >
            <div className="flex flex-col gap-1">
                {showBack && (
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-25 transition-colors active:scale-95 origin-left w-fit mb-2 min-w-[44px] min-h-[44px]"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                        <span className="text-sm font-medium">Geri</span>
                    </button>
                )}
                <motion.h1
                    style={{ scale: titleScale, y: titleY, originX: 0 }}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white tracking-tight origin-left"
                >
                    {title}
                    <span className="block text-[10px] font-black uppercase tracking-[0.4em] text-brand-accent mt-1 opacity-60">
                        {config.theme.vibe} Boutique
                    </span>
                </motion.h1>
            </div>

            {rightAction && (
                <div className="mb-1">
                    {rightAction}
                </div>
            )}
        </motion.div>
    );
};

export default PageHeader;
